import type { EnvironmentProviders, Provider } from '@angular/core';

/**
 * Estado compartido, sin dependencias de Angular/rxjs, para comunicar el
 * resultado del registro de chat-mfe (ver `federation-init.ts`) al mundo
 * Angular (`ChatFederationReadyService`) sin arrastrar `@angular/core` ni
 * `rxjs` —externalizados por `shareAll()` en federation.config.js— a un
 * bundle que se evalúa de forma estática antes de que `initFederation()`
 * inyecte el import map del host. Mismo motivo que `chat-theme-bridge.ts` /
 * `chat-lang-bridge.ts`.
 *
 * `federation-init.ts` es el único productor. `ChatFederationReadyService`
 * es el único consumidor real.
 */
export type ChatFederationOutcome =
  | { status: 'ready'; providers: (Provider | EnvironmentProviders)[] }
  | { status: 'failed' };

type Listener = (outcome: ChatFederationOutcome) => void;

let outcome: ChatFederationOutcome | null = null;
const listeners = new Set<Listener>();

/**
 * Registra un listener para el resultado (único, definitivo) del registro de
 * chat-mfe. Si ya se resolvió, se invoca de inmediato con el valor ya
 * conocido (efecto "replay"). Devuelve una función para des-registrar.
 */
export function onChatFederationSettled(listener: Listener): () => void {
  if (outcome) {
    listener(outcome);
    return () => {};
  }
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function resolveChatFederationProviders(providers: (Provider | EnvironmentProviders)[]): void {
  settle({ status: 'ready', providers });
}

export function rejectChatFederationProviders(): void {
  settle({ status: 'failed' });
}

function settle(next: ChatFederationOutcome): void {
  if (outcome) return; // Se resuelve una sola vez por carga de página.
  outcome = next;
  listeners.forEach(listener => listener(next));
  listeners.clear();
}
